import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { EDIT_DIALOG_CONFIGS } from './dialogConfigs';

const EditInfoDialog = ({ open, onOpenChange, dialogType, formData, setFormData, onSubmit, formLoading }) => {
  const config = EDIT_DIALOG_CONFIGS[dialogType];
  if (!config) return null;

  const handleFieldChange = (fieldName, value, fieldType) => {
    const processedValue = fieldType === 'number' ? (value ? parseFloat(value) : null) : value;
    setFormData({ ...formData, [fieldName]: processedValue });
  };

  const renderField = (field, index) => {
    if (field.type === 'divider') {
      return (
        <div key={index} className="border-t border-slate-200 pt-4 mt-4 col-span-2">
          <p className="text-xs text-slate-500 mb-3">{field.label}</p>
        </div>
      );
    }

    return (
      <div key={index} className="space-y-2">
        <Label>{field.label}</Label>
        {field.type === 'select' ? (
          <Select
            value={formData[field.name] || ''}
            onValueChange={(v) => handleFieldChange(field.name, v, field.type)}
          >
            <SelectTrigger>
              <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : field.type === 'textarea' ? (
          <Textarea
            value={formData[field.name] || ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value, field.type)}
            placeholder={field.placeholder}
            rows={field.rows || 3}
          />
        ) : (
          <Input
            type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : field.type === 'email' ? 'email' : 'text'}
            value={formData[field.name] || ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value, field.type)}
            placeholder={field.placeholder}
            min={field.min}
            max={field.max}
            step={field.step}
          />
        )}
      </div>
    );
  };

  const renderFields = () => {
    const elements = [];
    let gridFields = [];

    for (let i = 0; i < config.fields.length; i++) {
      const field = config.fields[i];
      if (field.grid === 2) {
        gridFields.push(field);
        if (gridFields.length === 2) {
          elements.push(
            <div key={`grid-${i}`} className="grid grid-cols-2 gap-4">
              {gridFields.map((f, j) => renderField(f, `${i}-${j}`))}
            </div>
          );
          gridFields = [];
        }
      } else {
        if (gridFields.length === 1) {
          elements.push(
            <div key={`grid-${i}`} className="grid grid-cols-2 gap-4">
              {renderField(gridFields[0], `${i}-0`)}
              <div></div>
            </div>
          );
          gridFields = [];
        }
        elements.push(renderField(field, i));
      }
    }

    if (gridFields.length === 1) {
      elements.push(
        <div key="grid-final" className="grid grid-cols-2 gap-4">
          {renderField(gridFields[0], 'final-0')}
          <div></div>
        </div>
      );
    }

    return elements;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={config.maxWidth || ''}>
        <DialogHeader>
          <DialogTitle className="font-serif">{config.title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <div className={`space-y-4 py-4 ${config.scrollable ? 'max-h-[60vh] overflow-y-auto' : ''}`}>
            {renderFields()}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={formLoading}>
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditInfoDialog;
